import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const PAGE_SIZES = [10, 25, 50];

export default function KeysPage() {
  const [keys, setKeys] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);

  const [form, setForm] = useState({ key_name: '', valid_days: '', device_limit: '' });
  const [generating, setGenerating] = useState(false);
  const [formError, setFormError] = useState('');

  const [modal, setModal] = useState(null);
  const [email, setEmail] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [modalError, setModalError] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  // Expire key OTP state
  const [expireTarget, setExpireTarget]     = useState(null); // key object
  const [expireOtpModal, setExpireOtpModal] = useState(false);
  const [expireOtp, setExpireOtp]           = useState('');
  const [expireSending, setExpireSending]   = useState(false);
  const [expireVerifying, setExpireVerifying] = useState(false);
  const [expireOtpError, setExpireOtpError] = useState('');

  const fetchKeys = useCallback(async (page = 1, limit = 10, q = '') => {
    try {
      const { data } = await api.get('/admin/keys', { params: { page, limit, search: q } });
      setKeys(data.data);
      setPagination(data.pagination);
    } catch {}
  }, []);

  useEffect(() => {
    fetchKeys(pagination.page, pagination.limit, search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKeys]);

  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      fetchKeys(1, pagination.limit, val);
    }, 400);
  };

  const goToPage = (p) => fetchKeys(p, pagination.limit, search);
  const changeLimit = (l) => fetchKeys(1, l, search);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setFormError('');
    setGenerating(true);
    try {
      await api.post('/admin/keys/generate', {
        key_name: form.key_name,
        valid_days: Number(form.valid_days),
        device_limit: Number(form.device_limit),
      });
      setForm({ key_name: '', valid_days: '', device_limit: '' });
      fetchKeys(1, pagination.limit, search);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const openModal = (k) => { setModal(k); setEmail(''); setModalError(''); };
  const closeModal = () => { setModal(null); setEmail(''); setModalError(''); };

  const handleAssign = async (e) => {
    e.preventDefault();
    setModalError('');
    setAssigning(true);
    try {
      await api.patch(`/admin/keys/${modal.id}/assign`, { email });
      closeModal();
      fetchKeys(pagination.page, pagination.limit, search);
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to assign email');
    } finally {
      setAssigning(false);
    }
  };

  const handleCopyKey = async (key) => {
  try {
    await navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => { setCopiedKey(null); }, 1500);
  } catch (error) {
    console.error('Failed to copy key:', error);
  }
  };

  const handleExpireClick = async (k) => {
    setExpireTarget(k);
    setExpireOtp('');
    setExpireOtpError('');
    setExpireSending(true);
    setExpireOtpModal(true);
    try {
      await api.post('/admin/keys/expire/send-otp', { key_id: k.id });
    } catch (err) {
      setExpireOtpError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setExpireSending(false);
    }
  };

  const handleResendExpireOtp = async () => {
    if (!expireTarget) return;
    setExpireSending(true);
    setExpireOtpError('');
    try {
      await api.post('/admin/keys/expire/send-otp', { key_id: expireTarget.id });
    } catch (err) {
      setExpireOtpError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setExpireSending(false);
    }
  };

  const handleVerifyExpireOtp = async (e) => {
    e.preventDefault();
    setExpireOtpError('');
    setExpireVerifying(true);
    try {
      await api.post('/admin/keys/expire/verify-otp', { otp: expireOtp, key_id: expireTarget.id });
      setExpireOtpModal(false);
      setExpireTarget(null);
      fetchKeys(pagination.page, pagination.limit, search);
    } catch (err) {
      setExpireOtpError(err.response?.data?.message || 'Invalid or expired OTP');
    } finally {
      setExpireVerifying(false);
    }
  };

  const closeExpireModal = () => { setExpireOtpModal(false); setExpireTarget(null); setExpireOtp(''); setExpireOtpError(''); };

  const { page, totalPages, total, limit } = pagination;
  const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRow   = Math.min(page * limit, total);

  // Page numbers to show
  const pageNums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) pageNums.push(i);

  return (
    <div>
      {/* Generate Key Form */}
      <div className="table-card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span className="card-title">🔑 Generate License Key</span>
        </div>
        <form onSubmit={handleGenerate} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', paddingTop: 8 }}>
          <div style={fs.field}>
            <label style={fs.label}>Valid Days</label>
            <input style={fs.input} type="number" min="1" placeholder="e.g. 30"
              value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} required />
          </div>
          {/* Key Name */} 
          <div style={fs.field}>
            <label style={fs.label}>Key Name</label>
            <select
              style={{
                ...fs.input,
                color: form.key_name === '' ? '#718096' : '#1a202c',
              }}
              value={form.key_name}
              onChange={(e) =>
                setForm({ ...form, key_name: e.target.value })
              }
              required
            >
              <option value="" style={{ color: '#718096' }}>
                Select Key Plan
              </option>
              {/* <option value="Basic">Basic</option>
              <option value="Standard">Standard</option>
              <option value="Premium">Premium</option>
              <option value="Deluxe">Deluxe</option> */}
              <option value="Secure Plus">Secure Plus</option>
              <option value="Secure Pro">Secure Pro</option>
              <option value="Secure Max">Secure Max</option>
            </select>
          </div>
          <div style={fs.field}>
            <label style={fs.label}>Device Limit</label>
            <input style={fs.input} type="number" min="1" placeholder="e.g. 3"
              value={form.device_limit} onChange={(e) => setForm({ ...form, device_limit: e.target.value })} required />
          </div>
          <div>
            <button className="logout-btn" style={{ background: '#2563eb', padding: '9px 22px', fontSize: 14 }} type="submit" disabled={generating}>
              {generating ? 'Generating...' : '+ Generate Key'}
            </button>
          </div>
          {formError && <p style={{ ...fs.error, width: '100%' }}>{formError}</p>}
        </form>
      </div>

      {/* Keys Table */}
      <div className="table-card">
        {/* Table toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="card-title">License Keys</span>
            <span style={{ fontSize: 13, color: '#718096', marginLeft: 6 }}>{total} total</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Per page */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={fs.label}>Show</span>
              <select style={{ ...fs.input, padding: '7px 10px', cursor: 'pointer' }}
                value={limit} onChange={(e) => changeLimit(Number(e.target.value))}>
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <span style={fs.searchIcon}>🔍</span>
              <input
                style={{ ...fs.input, paddingLeft: 30, width: 220 }}
                placeholder="Search key or email..."
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Key</th>
                <th>Key Name</th>
                <th>Valid Days</th>
                <th>Device Limit</th>
                <th>Remaining Days</th>
                <th>Assigned Email</th>
                <th>Status</th>
                <th>Created</th>
                <th>Action</th>
                <th>Expire</th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#a0aec0', padding: 32 }}>
                  {searchInput ? 'No keys match your search' : 'No keys generated yet'}
                </td></tr>
              )}
              {keys.map((k, i) => (
                <tr key={k.id}>
                  <td>{startRow + i}</td>
                  <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={fs.keyCode}>{k.key}</code>

                    <button
                      type="button"
                      onClick={() => handleCopyKey(k.key)}
                      title={copiedKey === k.key ? 'Copied' : 'Copy key'}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: copiedKey === k.key ? '#16a34a' : '#718096',
                        cursor: 'pointer',
                        fontSize: 16,
                        padding: '3px 5px',
                      }}
                    >
                      {copiedKey === k.key ? '✓' : '📋'}
                    </button>
                  </div>
                  </td>
                  <td>{k.key_name}</td>
                  <td>{k.valid_days}</td>
                  <td>{k.device_limit}</td>
                  <td>{k.remaining_days}</td>
                  <td style={{ color: k.email ? '#2d3748' : '#a0aec0' }}>{k.email || '—'}</td>
                  <td>
                    <span className={`status-badge ${k.status === 'activated' ? 'active' : 'expired'}`}>
                      {k.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{new Date(k.created_at).toLocaleDateString()}</td>
                  <td>
                    {k.email
                      ? <span style={fs.assignedTag}>✓ Assigned</span>
                      : <button style={fs.assignBtn} onClick={() => openModal(k)}>Assign</button>
                    }
                  </td>
                  <td>
                    <button
                      style={{
                        ...fs.expireBtn,
                        marginLeft: 8,
                        opacity: k.status === 'activated' ? 1 : 0.5,
                        cursor: k.status === 'activated' ? 'pointer' : 'not-allowed',
                      }}
                      onClick={() => k.status === 'activated' && handleExpireClick(k)}
                      disabled={k.status !== 'activated'}
                    >
                      Expire
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div style={fs.paginationWrap}>
            <span style={fs.pageInfo}>
              Showing {startRow}–{endRow} of {total}
            </span>
            <div style={fs.pageButtons}>
              <button style={fs.pageBtn} disabled={page === 1} onClick={() => goToPage(1)}>«</button>
              <button style={fs.pageBtn} disabled={page === 1} onClick={() => goToPage(page - 1)}>‹</button>
              {pageNums.map(n => (
                <button key={n}
                  style={{ ...fs.pageBtn, ...(n === page ? fs.pageBtnActive : {}) }}
                  onClick={() => goToPage(n)}>{n}</button>
              ))}
              <button style={fs.pageBtn} disabled={page === totalPages} onClick={() => goToPage(page + 1)}>›</button>
              <button style={fs.pageBtn} disabled={page === totalPages} onClick={() => goToPage(totalPages)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Assign Email Modal */}
      {modal && (
        <div style={fs.backdrop} onClick={closeModal}>
          <div style={fs.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={fs.modalHeader}>
              <span style={fs.modalTitle}>Assign Email to Key</span>
              <button style={fs.closeBtn} onClick={closeModal}>✕</button>
            </div>
            <div style={fs.modalBody}>
              <p style={fs.keyDisplay}>{modal.key}</p>
              <form onSubmit={handleAssign}>
                <label style={fs.label}>Email Address</label>
                <input
                  style={{ ...fs.input, width: '100%', marginTop: 6, marginBottom: 12 }}
                  type="email" placeholder="user@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required autoFocus
                />
                {modalError && <p style={fs.error}>{modalError}</p>}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" style={fs.cancelBtn} onClick={closeModal}>Cancel</button>
                  <button type="submit" style={fs.saveBtn} disabled={assigning}>
                    {assigning ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Expire Key OTP Modal */}
      {expireOtpModal && (
        <div style={fs.backdrop} onClick={closeExpireModal}>
          <div style={fs.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={fs.modalHeader}>
              <span style={fs.modalTitle}>🔒 Expire License Key</span>
              <button style={fs.closeBtn} onClick={closeExpireModal}>✕</button>
            </div>
            <div style={fs.modalBody}>
              {expireTarget && (
                <p style={fs.keyDisplay}>{expireTarget.key}</p>
              )}
              {expireSending ? (
                <p style={{ textAlign: 'center', color: '#718096', fontSize: 14 }}>Sending OTP...</p>
              ) : (
                <>
                  <p style={{ fontSize: 14, color: '#4a5568', marginBottom: 16 }}>
                    An OTP has been sent to <strong>507shrutisharma@gmail.com</strong>. Enter it below to expire this key.
                  </p>
                  <form onSubmit={handleVerifyExpireOtp}>
                    <label style={fs.label}>Enter OTP</label>
                    <input
                      style={{ ...fs.input, width: '100%', marginTop: 6, marginBottom: 4, letterSpacing: 6, fontSize: 20, textAlign: 'center' }}
                      type="text" inputMode="numeric" maxLength={6} placeholder="------"
                      value={expireOtp} onChange={(e) => setExpireOtp(e.target.value.replace(/\D/g, ''))}
                      autoFocus required
                    />
                    {expireOtpError && <p style={{ ...fs.error, marginBottom: 10 }}>{expireOtpError}</p>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                      <button type="button" style={fs.resendBtn} disabled={expireSending} onClick={handleResendExpireOtp}>
                        {expireSending ? 'Resending...' : 'Resend OTP'}
                      </button>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" style={fs.cancelBtn} onClick={closeExpireModal}>Cancel</button>
                        <button type="submit" style={fs.expireSaveBtn} disabled={expireVerifying || expireOtp.length < 6}>
                          {expireVerifying ? 'Verifying...' : 'Confirm Expire'}
                        </button>
                      </div>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const fs = {
  field:       { display: 'flex', flexDirection: 'column', minWidth: 140 },
  label:       { fontSize: 13, color: '#4a5568', marginBottom: 5, fontWeight: 500 },
  input:       { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, outline: 'none' },
  error:       { color: '#dc2626', fontSize: 13, marginTop: 4 },
  keyCode:     { background: '#f1f5f9', padding: '3px 8px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', letterSpacing: 1 },
  assignBtn:   { padding: '5px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 },
  assignedTag: { padding: '4px 12px', background: '#dcfce7', color: '#16a34a', borderRadius: 5, fontSize: 13, fontWeight: 500 },
  searchIcon:  { position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, pointerEvents: 'none' },

  paginationWrap: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 16, paddingTop: 14, borderTop: '1px solid #e2e8f0' },
  pageInfo:       { fontSize: 13, color: '#718096' },
  pageButtons:    { display: 'flex', gap: 4 },
  pageBtn:        { minWidth: 34, height: 34, padding: '0 10px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#4a5568', transition: 'all 0.15s' },
  pageBtnActive:  { background: '#2563eb', color: '#fff', borderColor: '#2563eb', fontWeight: 600 },

  backdrop:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalBox:    { background: '#fff', borderRadius: 10, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' },
  modalTitle:  { fontSize: 16, fontWeight: 600, color: '#2d3748' },
  closeBtn:    { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#718096', lineHeight: 1 },
  modalBody:   { padding: '20px' },
  keyDisplay:  { background: '#f1f5f9', padding: '10px 14px', borderRadius: 6, fontFamily: 'monospace', fontSize: 14, letterSpacing: 1, marginBottom: 16, color: '#2563eb', textAlign: 'center', fontWeight: 600 },
  cancelBtn:   { padding: '8px 20px', background: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  saveBtn:     { padding: '8px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  expireBtn:   { padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12 },
  expireSaveBtn: { padding: '8px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  resendBtn:   { background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline' },
};
